import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Pencil,
  Trash2,
  Check,
  X,
  Plus,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChanged: () => void;
}

export function CategoryManagerDialog({ open, onOpenChange, onChanged }: Props) {
  const [cats, setCats] = useState<Category[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<Category>>({});
  const [reordering, setReordering] = useState(false);

  const load = async () => {
    const [c, i] = await Promise.all([
      supabase.from("categories").select("*").order("sort_order"),
      supabase.from("images").select("category_id"),
    ]);
    if (c.data) setCats(c.data);
    if (i.data) {
      const m: Record<string, number> = {};
      for (const row of i.data) m[row.category_id] = (m[row.category_id] ?? 0) + 1;
      setCounts(m);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const add = async () => {
    const s = slug.trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{1,30}$/.test(s))
      return toast.error("Slug 需為小寫英數/底線，且以英文字母開頭");
    if (!name.trim()) return toast.error("請輸入名稱");
    const order = sortOrder ? Number(sortOrder) : (cats.at(-1)?.sort_order ?? 100) + 10;
    const { error } = await supabase
      .from("categories")
      .insert({ name: name.trim(), slug: s, sort_order: order });
    if (error) return toast.error(error.message);
    toast.success(`已新增 ${name}`);
    setName("");
    setSlug("");
    setSortOrder("");
    await load();
    onChanged();
  };

  const startEdit = (c: Category) => {
    setEditingId(c.id);
    setEditDraft({ name: c.name, slug: c.slug, sort_order: c.sort_order });
  };

  const saveEdit = async (id: string) => {
    const s = (editDraft.slug ?? "").trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{1,30}$/.test(s)) return toast.error("Slug 格式不正確");
    if (!editDraft.name?.trim()) return toast.error("請輸入名稱");
    const { error } = await supabase
      .from("categories")
      .update({
        name: editDraft.name.trim(),
        slug: s,
        sort_order: Number(editDraft.sort_order) || 100,
      })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("已更新");
    setEditingId(null);
    await load();
    onChanged();
  };

  const remove = async (c: Category) => {
    if ((counts[c.id] ?? 0) > 0)
      return toast.error(`無法刪除 — 此分類還有 ${counts[c.id]} 張圖片`);
    if (!confirm(`確定刪除分類「${c.name}」？`)) return;
    const { error } = await supabase.from("categories").delete().eq("id", c.id);
    if (error) return toast.error(error.message);
    toast.success("已刪除");
    await load();
    onChanged();
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= cats.length) return;
    const a = cats[index];
    const b = cats[target];
    setReordering(true);
    // Optimistic UI
    const next = [...cats];
    next[index] = { ...b, sort_order: a.sort_order };
    next[target] = { ...a, sort_order: b.sort_order };
    // Normalize so both stay distinct even when equal
    if (a.sort_order === b.sort_order) {
      next[index] = { ...b, sort_order: a.sort_order };
      next[target] = { ...a, sort_order: a.sort_order - 1 };
    }
    setCats(next.sort((x, y) => x.sort_order - y.sort_order));

    const { error: e1 } = await supabase
      .from("categories")
      .update({ sort_order: next.find((c) => c.id === a.id)!.sort_order })
      .eq("id", a.id);
    const { error: e2 } = await supabase
      .from("categories")
      .update({ sort_order: next.find((c) => c.id === b.id)!.sort_order })
      .eq("id", b.id);
    setReordering(false);
    if (e1 || e2) {
      toast.error("排序失敗");
      await load();
      return;
    }
    await load();
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>管理分類</DialogTitle>
          <DialogDescription>
            新增、編輯、排序或刪除主要分類。
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_100px_auto] gap-2 items-end border-b border-border pb-4">
          <div>
            <Label className="text-xs">中文名稱</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="例：生活作息" />
          </div>
          <div>
            <Label className="text-xs">英文 slug</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="例：routine"
            />
          </div>
          <div>
            <Label className="text-xs">排序</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="自動"
            />
          </div>
          <Button onClick={add}>
            <Plus className="h-4 w-4 mr-1" /> 新增
          </Button>
        </div>

        <div className="max-h-[50vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">順序</TableHead>
                <TableHead>名稱</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead className="w-20">排序</TableHead>
                <TableHead className="w-20">圖片數</TableHead>
                <TableHead className="w-24 text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cats.map((c, idx) => {
                const editing = editingId === c.id;
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={idx === 0 || reordering}
                          onClick={() => move(idx, -1)}
                          aria-label="上移"
                          className="h-7 w-7"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={idx === cats.length - 1 || reordering}
                          onClick={() => move(idx, 1)}
                          aria-label="下移"
                          className="h-7 w-7"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <Input
                          value={editDraft.name ?? ""}
                          onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        />
                      ) : (
                        c.name
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {editing ? (
                        <Input
                          value={editDraft.slug ?? ""}
                          onChange={(e) => setEditDraft({ ...editDraft, slug: e.target.value })}
                        />
                      ) : (
                        c.slug
                      )}
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <Input
                          type="number"
                          value={editDraft.sort_order ?? 0}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, sort_order: Number(e.target.value) })
                          }
                        />
                      ) : (
                        c.sort_order
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{counts[c.id] ?? 0}</TableCell>
                    <TableCell className="text-right">
                      {editing ? (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => saveEdit(c.id)}>
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingId(null)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => startEdit(c)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(c)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
